/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useState
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {
  EntityStatus,
  GenericObject
} from '@ion/core/src/types/entities';
import EmbeddedForm from '@ion/dls/src/editors/forms/EmbeddedForm';
import {validateServices} from '../pages/devices/DeviceServicesUtils';
import ServiceEntry from '../pages/devices/ServiceEntry';

interface ServiceEnablementFormProps {
  setTaskNickNameAction: (action: string) => void;
  onSave: (details: ServiceDetails) => void;
  services?: string[];
}

export type ServiceDetails = {
  features: {
    [key: string]: string;
  };
}

export interface Entry {
  id: number;
  service: GenericObject;
}

/**
 * Re-usable form with the details for Service Enablement of an entity.
 */
export const ServiceEnablementForm = forwardRef(({
  setTaskNickNameAction,
  services,
  onSave
}: ServiceEnablementFormProps, ref) => {
  const serviceOptions                  = services?.sort();
  const newEntry: Entry                 = {
    id      : 1,
    service : {
      name    : '',
      status  : EntityStatus.Enabled,
      options : serviceOptions || [],
      error   : false
    }
  };
  const [serviceEntry, setServiceEntry] = useState<Entry>(newEntry);

  const validateForm = function() {
    const {isValid} = validateServices([serviceEntry]);
    if (isValid) {
      return true;
    }
    return false;
  };

  const getServiceName = useCallback((serviceName) => {
    const nameArr = serviceName.split(' ');
    if (nameArr?.length > 1) {
      return nameArr[0].toUpperCase();
    }
    return serviceName;
  }, []);

  const updateEntry = useCallback(function(id: number, name: string, value: string): void {
    const updatedEntry = {...serviceEntry};
    updatedEntry.service[name] = value;
    updatedEntry.service.error = false;
    setServiceEntry(updatedEntry);
    setTaskNickNameAction(getTaskNickNameAction(updatedEntry));
  }, [serviceEntry]);

  const onSubmit = function() {
    const valid = validateForm();
    if (valid) {
      const {name, status} = serviceEntry.service;
      const serviceName = getServiceName(name);
      onSave({
        features : {[serviceName]: status}
      });
    } else {
      const updatedEntry = {...serviceEntry};
      updatedEntry.service.error = true;
      setServiceEntry(updatedEntry);
    }
  };

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  return (
    <EmbeddedForm>
      <ServiceEntry fullWidth
        id={1}
        onUpdate={updateEntry}
        service={serviceEntry?.service} />
    </EmbeddedForm>
  );
});

const getTaskNickNameAction = (entry: Entry) => {
  const {service: {name, status}} = entry;
  const action = translate(`common:entityAction.${status === EntityStatus.Enabled ? 'enable' : 'disable'}`);
  return `- ${action} ${name}`;
};

export default memo(ServiceEnablementForm);
